import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import Swal from "sweetalert2";
import { FiPlus } from "react-icons/fi";
import {
  EmptyState,
  ItemCard,
  PageHeader,
  STATUS_LABELS,
} from "../../components/dashboard/DashboardUI";
import Loader from "../../components/loader/Loader";
import ProcedureResponseModal from "../../components/modals/ProcedureResponseModal";
import RequestDetail from "../../components/modals/RequestDetail";
import { fetchUserProcedures } from "../../redux/actions/ProcedureActions";
import {
  getImportationRequests,
  getOapiRequests,
} from "../../redux/actions/FormalityActions";
import { REQUEST_TYPES, requestTitle } from "../../utils/requestDisplay";
import { isStale, isUnpaid, resumePayment } from "../../utils/resumePayment";

const STATUS_FILTERS = [
  "initiated",
  "paid",
  "in_progress",
  "fulfilled",
  "failed",
];

function Procedures() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.authUser);
  const { procedures } = useSelector((state) => state.procedures);
  const { activeCompany } = useSelector((state) => state.companies);

  const [loading, setLoading] = useState(true);
  const [companyRequests, setCompanyRequests] = useState([]);
  const [search, setSearch] = useState("");
  const [searchParams] = useSearchParams();
  // The home page links here with the unpaid requests already filtered
  const [status, setStatus] = useState(searchParams.get("status") || "");
  const [paying, setPaying] = useState(false);
  const [type, setType] = useState("");
  const [selected, setSelected] = useState(null);
  const [showResponse, setShowResponse] = useState(false);

  useEffect(() => {
    if (!user) return;

    const companyId = activeCompany?._id;
    const fetchCompanyRequests = (action) =>
      new Promise((resolve) => {
        if (!companyId) return resolve([]);
        dispatch(
          action(user.token, companyId, (data) =>
            resolve(Array.isArray(data) ? data : []),
          ),
        );
      });

    setLoading(true);
    Promise.all([
      dispatch(fetchUserProcedures(user.token)),
      fetchCompanyRequests(getImportationRequests),
      fetchCompanyRequests(getOapiRequests),
    ])
      .then(([, importations, oapis]) =>
        setCompanyRequests([...importations, ...oapis]),
      )
      .catch(() => setCompanyRequests([]))
      .finally(() => setLoading(false));
  }, [user, activeCompany, dispatch]);

  const requests = useMemo(() => {
    const byReference = new Map();

    // Unpaid requests left for too long are no longer listed
    [...(procedures || []), ...companyRequests]
      .filter((request) => !isStale(request))
      .forEach((request) => byReference.set(request.reference, request));

    return [...byReference.values()].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
    );
  }, [procedures, companyRequests]);

  const types = [...new Set(requests.map((request) => request.type))];

  const filtered = requests.filter((request) => {
    const term = search.trim().toLowerCase();
    const matchesSearch =
      !term ||
      request.reference?.toLowerCase().includes(term) ||
      requestTitle(request).toLowerCase().includes(term);

    return (
      matchesSearch &&
      (!status || request.status === status) &&
      (!type || request.type === type)
    );
  });

  // A request left unpaid can be paid at any time, at today's price
  const pay = async (request) => {
    setPaying(true);
    const error = await resumePayment({
      dispatch,
      navigate,
      token: user.token,
      request,
    });
    setPaying(false);

    if (error) {
      Swal.fire({ icon: "error", title: "Paiement impossible", text: error });
    }
  };

  return (
    <>
      <Loader isLoading={paying} />
      <PageHeader
        title="Mes démarches"
        lead="Suivez l'avancement de toutes vos demandes."
        actions={
          <button
            type="button"
            className="dash-button"
            onClick={() => navigate("/formalities")}
          >
            <FiPlus aria-hidden="true" /> Nouvelle démarche
          </button>
        }
      />

      <div className="dash-card">
        <div className="dash-filters">
          <input
            type="search"
            placeholder="Rechercher une référence ou une démarche"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Rechercher"
          />
          <select
            value={type}
            onChange={(event) => setType(event.target.value)}
            aria-label="Type de démarche"
          >
            <option value="">Tous les types</option>
            {types.map((value) => (
              <option key={value} value={value}>
                {REQUEST_TYPES[value] || value}
              </option>
            ))}
          </select>
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            aria-label="Statut"
          >
            <option value="">Tous les statuts</option>
            {STATUS_FILTERS.map((value) => (
              <option key={value} value={value}>
                {STATUS_LABELS[value]}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <p className="dash-loading">Chargement de vos démarches…</p>
        ) : filtered.length === 0 ? (
          <EmptyState title="Aucune démarche">
            {requests.length === 0
              ? "Vos démarches apparaîtront ici."
              : "Aucune démarche ne correspond à votre recherche."}
          </EmptyState>
        ) : (
          <div className="dash-items">
            {filtered.map((request) => (
              <ItemCard
                key={request.reference}
                title={requestTitle(request)}
                meta={`#${request.reference} · ${moment(
                  request.createdAt,
                ).format("DD/MM/YYYY")}`}
                status={request.status}
                actions={
                  <>
                    {request.comment && (
                      <button
                        type="button"
                        className="dash-link-button"
                        onClick={() => {
                          setSelected(request);
                          setShowResponse(true);
                        }}
                      >
                        Réponse
                      </button>
                    )}
                    <button
                      type="button"
                      className="dash-link-button"
                      onClick={() => setSelected(request)}
                    >
                      Voir
                    </button>
                    {isUnpaid(request) && (
                      <button
                        type="button"
                        className="dash-link-button is-primary"
                        onClick={() => pay(request)}
                      >
                        Payer
                      </button>
                    )}
                  </>
                }
              />
            ))}
          </div>
        )}
      </div>

      {!showResponse && (
        <RequestDetail request={selected} onHide={() => setSelected(null)} />
      )}

      <ProcedureResponseModal
        show={showResponse}
        onHide={() => {
          setShowResponse(false);
          setSelected(null);
        }}
        procedure={selected}
      />
    </>
  );
}

export default Procedures;
